import React, { useEffect, useRef, useState } from 'react';

const StatItem = ({ number, label, index }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const node = elementRef.current;

    if (typeof window.IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const target = parseInt(number.replace(/\D/g, ''), 10);
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

      if (prefersReducedMotion) {
        setCount(target);
        return;
      }

      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isVisible, number]);

  const displayNumber = number.includes('+') ? `${count}+` : count;

  return (
    <article
      ref={elementRef}
      className="stat-item fade-in p-4 md:p-6 relative group bg-primary-green-dark/30 rounded-2xl border border-primary-green-light/30 backdrop-blur-md"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-accent-gold/10 to-primary-green-light/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true"></div>
      <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-accent-gold/20 to-primary-green-light/20 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" aria-hidden="true"></div>

      <div className="relative z-10">
        <span className="stat-number text-4xl md:text-5xl lg:text-6xl font-extrabold block group-hover:scale-110 transition-transform duration-500" aria-label={`${number} ${label}`}>
          {isVisible ? displayNumber : '0'}
        </span>
        <h3 className="text-xs md:text-base lg:text-lg mt-2 md:mt-4 text-primary-green-light/90 group-hover:text-white transition-colors duration-300">
          {label}
        </h3>

        <div className="w-full h-1 bg-primary-green-dark rounded-full mt-4 overflow-hidden" aria-hidden="true">
          <div
            className="h-full bg-gradient-to-r from-accent-gold to-primary-green-light transition-all duration-2000 ease-out"
            style={{ width: isVisible ? '100%' : '0%' }}
          ></div>
        </div>
      </div>
    </article>
  );
};

const Stats = () => {
  const stats = [
    { number: '8+', label: 'Years Company Operation' },
    { number: '30+', label: 'Years Engineering Experience' },
    { number: '4', label: 'Advanced VMC Machines' },
    { number: '15+', label: 'Industries Served Overall' }
  ];

  return (
    <section className="stats py-12 md:py-24 px-[5%] bg-gradient-to-b from-primary-green-dark to-primary-dark relative overflow-hidden" aria-labelledby="stats-heading">
      <h2 id="stats-heading" className="sr-only">Jay Ambe Engineering experience and machining capacity</h2>
      <div className="absolute inset-0 opacity-15" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary-green-light rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent-gold rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center max-w-6xl mx-auto">
        {stats.map((stat, index) => (
          <StatItem key={index} number={stat.number} label={stat.label} index={index} />
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <div className="w-32 h-1 bg-gradient-to-r from-transparent via-accent-gold to-transparent rounded-full" aria-hidden="true"></div>
      </div>
    </section>
  );
};

export default Stats;
